/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from "react";

import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { CommandBar, ICommandBarItemProps, Stack } from "@fluentui/react";
import { Text } from "@fluentui/react/lib/Text";

import { errorMessageStyles } from "Components/AddComment/AddComment.styles";
import { AwardCategoriesUpdatePanel } from "Components/AwardCategoriesStatusUpdatePanel/AwardCategoriesUpdatePanel";
import { Title } from "Components/Common/Results/Result.styles";
import { SubSiteUrlRootPath } from "Constants/TenantTypes";
import {
  commandBarStyles,
  containerStackStyles,
  listStackStyles,
  tableStackStyles,
  PaddedFlexRow,
} from "Containers/Common/CommonContainer.styles";
import { getSelectedAwardCategory } from "Dataflows/AwardCategories/AwardCategoriesSlice";

import AwardCategories from "../../Components/AwardCategories/AwardCategories";
import { IAwardCategoriesContainerMatchProps } from "./IAwardCategoriesContainerMatchProps";

const AwardCategoriesContainer = () => {
  const [isStatusUpdatePanelOpen, setStatusUpdatePanelOpen] = useState(false);
  const routeParams = useParams<IAwardCategoriesContainerMatchProps>(); // Update interface
  const tenant = SubSiteUrlRootPath[routeParams.tenant];
  const selectedAwardCategory = useSelector(getSelectedAwardCategory);

  const [selectCategoryMessage, setSelectCategoryMessage] = useState<string>("");

  //To reset the error message when the selected award category is changed
  useEffect(() => {
    if (selectedAwardCategory) {
      setSelectCategoryMessage("");
    }
  }, [selectedAwardCategory]);

  const commandBarItems: ICommandBarItemProps[] = [];

  // Check if the selected award category is valid
  const isCategorySelected = (): boolean => {
    // Check if it's non-null and has a valid technologyFocusAreaGroupId
    return selectedAwardCategory != null && !!selectedAwardCategory.technologyFocusAreaGroupId;
  };

  const onModifyClick = () => {
    if (isCategorySelected()) {
      setStatusUpdatePanelOpen(true);
    } else {
      setSelectCategoryMessage("Please select an award category to modify");
    }
  };

  commandBarItems.push({
    key: "updateStatus",
    text: "Modify",
    iconProps: { iconName: "Edit" },
    onClick: (_, item) => onModifyClick(),
  });

  return (
    <>
      <Stack horizontal styles={containerStackStyles}>
        <Stack.Item grow styles={listStackStyles()}>
          <Stack styles={tableStackStyles} tokens={{ childrenGap: "1.5rem" }}>
            <PaddedFlexRow>
              <Title>Program Technology Structure</Title>
            </PaddedFlexRow>

            <CommandBar
              items={commandBarItems}
              ariaLabel="Award Categories actions"
              styles={commandBarStyles}
            />
            {selectCategoryMessage && (
              <Text styles={errorMessageStyles}>{selectCategoryMessage}</Text>
            )}

            <AwardCategories tenant={tenant} />
          </Stack>
        </Stack.Item>
      </Stack>
      {isStatusUpdatePanelOpen && (
        <AwardCategoriesUpdatePanel
          isOpen={isStatusUpdatePanelOpen}
          setIsOpen={setStatusUpdatePanelOpen}
        />
      )}
    </>
  );
};

export default AwardCategoriesContainer;
